import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { some } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';

import { ComponentAbstract } from '@app/components/abstract/component.abstract';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { MainPageActions } from '@shared/enums/main-page-actions';
import { RoutePath } from '@shared/enums/route-path.enum';
import { MenuSection } from '@shared/interfaces/menu-config.interface';
import { HelpRouterService } from '@shared/services/help-router.service';

@UntilDestroy()
@Component({
  selector: 'app-side-panel-section',
  templateUrl: './side-panel-section.component.html',
  styleUrls: ['./side-panel-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidePanelSectionComponent extends ComponentAbstract implements OnInit, OnDestroy {
  @Input() section: MenuSection;
  @Input() currentSectionBSubject: BehaviorSubject<string>;
  @Output() menuActionsEvent: EventEmitter<MainPageActions> = new EventEmitter<MainPageActions>();

  homePaths = [
    AppRoutes.ROLES_ADMIN_HOME,
    AppRoutes.ROLES_ASSET_HOME,
    AppRoutes.ROLES_CONSTRUCTION_HOME,
    AppRoutes.ROLES_PROPERTY_HOME,
    AppRoutes.ROLES_VENDOR_HOME,
  ];

  constructor(protected cdr: ChangeDetectorRef, private router: Router, private helpRouterService: HelpRouterService) {
    super(cdr);
  }

  ngOnInit(): void {
    this.checkActiveSection(window.location.pathname);
    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(event => event instanceof NavigationEnd),
        distinctUntilChanged(),
        tap((event: NavigationEnd) => {
          this.checkActiveSection(event.url);
        })
      )
      .subscribe();

    this.currentSectionBSubject
      ?.pipe(
        untilDestroyed(this),
        tap(currentSection => {
          this.isExpanded = currentSection === this.section.label;
          this.cdr.detectChanges();
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {}

  open() {
    const queryParams = this.helpRouterService.getGlobalPreservedParams();
    if (this.section.urlPath) {
      this.router.navigate([this.section.urlPath], { queryParams }).then();
    }
  }

  toggleSection(event: MouseEvent) {
    event.stopPropagation();
    let currentSection = null;
    if (!this.isExpanded) {
      currentSection = this.section.label;
    }
    this.currentSectionBSubject?.next(currentSection);
  }

  private checkActiveSection(path: string) {
    if (this.section.urlPath === RoutePath.HOME) {
      this.isActive = some(this.homePaths, item => path.startsWith(`/${item}`));
    } else {
      this.isActive =
        path.startsWith(`${this.section.urlPath}`) ||
        some(
          this.section.sectionItem,
          sectionItem => !sectionItem.separator && path.startsWith(`${sectionItem.urlPath}`)
        );
    }

    this.cdr.detectChanges();
  }

  menuActions($event: MainPageActions) {
    this.menuActionsEvent.emit($event);
  }
}
